import { useId } from 'react'

/**
 * One setting that is on or off.
 *
 * **It is deliberately neither of the two things it could have been.** Not a browser checkbox: those
 * look like nothing else in MyLib, cannot be sized to the target every other control keeps, and drag
 * a platform's own styling into a screen. And not a chip either — a chip is a *filter*, it sits in a
 * row with its siblings so its state can be read by comparison, and on its own it reads as a button
 * waiting to be pressed. A lone setting rendered that way invites exactly the wrong gesture: somebody
 * presses what is already on and turns it off.
 *
 * So it is drawn as a switch, and its state is legible three ways over: the knob is on one side or
 * the other, the track is filled or not, and the word beside it says **On** or **Off** in as many
 * letters. Colour alone is never the answer to "is this on".
 *
 * `role="switch"` with `aria-checked` is what it is to a screen reader — the one ARIA role that means
 * exactly this and nothing else.
 */
export interface SwitchProps {
  /** What the setting is, as a thing rather than as a command: "Merge double periods". */
  readonly label: string
  /** One sentence under it, saying what being on means. Optional. */
  readonly hint?: string
  readonly on: boolean
  readonly onChange: (on: boolean) => void
  readonly disabled?: boolean
}

export function Switch({ label, hint, on, onChange, disabled = false }: SwitchProps) {
  const base = useId()

  return (
    <div className="switch">
      <button
        type="button"
        role="switch"
        className="switch__control"
        aria-checked={on}
        aria-describedby={hint === undefined ? undefined : `${base}-hint`}
        disabled={disabled}
        onClick={() => {
          onChange(!on)
        }}
      >
        <span className="switch__track" aria-hidden="true">
          <span className="switch__knob" />
        </span>
        <span className="switch__label">{label}</span>
        {/* The state in words. A switch read by colour and position alone is a switch somebody has
            to experiment with, and this is a setting they meet once and then trust. */}
        <span className="switch__state">{on ? 'On' : 'Off'}</span>
      </button>
      {hint !== undefined && (
        <p className="switch__hint" id={`${base}-hint`}>
          {hint}
        </p>
      )}
    </div>
  )
}
